<template>
  <v-bottom-sheet v-model="model" max-width="600">
    <section class="rm-theme rm-sheet" :aria-label="$t('filters')">
      <div class="rm-sheet__body">
        <div class="rm-sheet__handle"></div>

        <div class="rm-sheet__header">
          <h2>{{ $t("filters") }}</h2>
          <button type="button" class="rm-text-btn" @click="resetDraft">
            {{ $t("report_mobile.reset") }}
          </button>
        </div>

        <div class="rm-sheet__group">
          <h3 class="rm-sheet__label">{{ $t("report_mobile.period") }}</h3>
          <div class="rm-filter-wrap">
            <button
              v-for="preset in quickPresets"
              :key="preset.key"
              type="button"
              class="rm-chip rm-chip--lg"
              :class="{ 'rm-chip--active': draftPresetKey === preset.key }"
              :aria-pressed="String(draftPresetKey === preset.key)"
              @click="selectPreset(preset)"
            >
              {{ $t(`report_mobile.presets.${preset.key}`) }}
            </button>
            <button
              v-if="!draftPresetKey"
              type="button"
              class="rm-chip rm-chip--lg rm-chip--active rm-num"
              aria-pressed="true"
              @click="$emit('open-period')"
            >
              {{ draftRangeText }}
            </button>
            <button
              type="button"
              class="rm-chip rm-chip--lg rm-chip--dashed"
              @click="$emit('open-period')"
            >
              {{ $t("report_mobile.pick_dates") }}
            </button>
          </div>
        </div>

        <div v-if="showShifts" class="rm-sheet__group">
          <h3 class="rm-sheet__label">{{ $t("shifts") }}</h3>
          <div class="rm-filter-shifts">
            <button
              v-for="shift in allShifts"
              :key="shift"
              type="button"
              class="rm-filter-shift"
              :class="{ 'is-active': draftShifts.includes(shift) }"
              :aria-pressed="String(draftShifts.includes(shift))"
              :aria-label="$t('report_mobile.shift_n', { n: shift })"
              @click="toggleShift(shift)"
            >
              {{ shift }}
            </button>
          </div>
        </div>

        <label class="rm-filter-view">
          <span class="rm-muted">{{ $t("view") }}</span>
          <span class="rm-filter-view__value">
            <select v-model.number="draftView" class="rm-filter-view__select">
              <option v-for="view in views" :key="view.id" :value="view.id">
                {{ view.name }}
              </option>
            </select>
            <v-icon size="18" color="#5f5852">mdi-chevron-down</v-icon>
          </span>
        </label>

        <div v-if="showCompanies" class="rm-sheet__group">
          <div class="rm-filter-companies__header">
            <h3 class="rm-sheet__label">{{ $t("companies") }}</h3>
            <span class="rm-muted rm-num">
              {{
                $t("report_mobile.selected_of", {
                  selected: draftCompanyIds.length,
                  total: companies.length,
                })
              }}
            </span>
          </div>

          <label class="rm-search rm-search--soft">
            <v-icon size="18" color="#5f5852">mdi-magnify</v-icon>
            <span class="rm-visually-hidden">{{ $t("report_mobile.search_companies") }}</span>
            <input
              v-model="companySearch"
              type="search"
              :placeholder="$t('report_mobile.search_companies')"
            />
          </label>

          <div class="rm-filter-companies">
            <label class="rm-filter-company rm-filter-company--all">
              <input
                type="checkbox"
                :checked="allCompaniesSelected"
                :indeterminate.prop="someCompaniesSelected"
                @change="toggleAllCompanies"
              />
              {{ $t("display_all") }}
            </label>
            <label
              v-for="company in visibleCompanies"
              :key="company.id"
              class="rm-filter-company"
            >
              <input
                type="checkbox"
                :checked="draftCompanyIds.includes(company.id)"
                @change="toggleCompany(company.id)"
              />
              {{ toLatin(company.name) }}
            </label>
            <p v-if="!visibleCompanies.length" class="rm-muted rm-filter-companies__empty">
              {{ $t("report_mobile.no_results") }}
            </p>
            <button
              v-if="hiddenCompaniesCount > 0"
              type="button"
              class="rm-text-btn rm-filter-companies__more"
              @click="showAllCompanies = true"
            >
              {{ $t("report_mobile.show_all_n", { n: companies.length }) }}
            </button>
          </div>
        </div>
      </div>

      <div class="rm-sheet__footer">
        <button type="button" class="rm-btn rm-btn--primary" @click="apply">
          {{ $t("report_mobile.show_results") }}
        </button>
      </div>
    </section>
  </v-bottom-sheet>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import { findPresetKey, formatRange } from "./periods";

const ALL_SHIFTS = [1, 2, 3, 4];
const COLLAPSED_COMPANY_COUNT = 5;

export default {
  name: "ReportMobileFilterSheet",
  mixins: [hasModel],
  props: {
    from: {
      type: String,
      required: true,
    },
    to: {
      type: String,
      required: true,
    },
    presets: {
      type: Array,
      required: true,
    },
    shifts: {
      type: Array,
      required: true,
    },
    showShifts: {
      type: Boolean,
      default: true,
    },
    showCompanies: {
      type: Boolean,
      default: true,
    },
    companies: {
      type: Array,
      default: () => [],
    },
    selectedCompanyIds: {
      type: Array,
      default: () => [],
    },
    views: {
      type: Array,
      default: () => [],
    },
    selectedView: {
      type: Number,
      default: 1,
    },
  },
  data: () => ({
    allShifts: ALL_SHIFTS,
    draftFrom: "",
    draftTo: "",
    draftShifts: [],
    draftCompanyIds: [],
    draftView: 1,
    companySearch: "",
    showAllCompanies: false,
  }),
  computed: {
    quickPresets() {
      return this.presets.filter((preset) => preset.key !== "last_month");
    },
    draftPresetKey() {
      return findPresetKey(this.presets, this.draftFrom, this.draftTo);
    },
    draftRangeText() {
      return formatRange(this.draftFrom, this.draftTo);
    },
    filteredCompanies() {
      const search = this.normalize(this.companySearch);
      if (!search) {
        return this.companies;
      }
      return this.companies.filter((company) =>
        this.normalize(company.name).includes(search)
      );
    },
    visibleCompanies() {
      if (this.showAllCompanies || this.companySearch) {
        return this.filteredCompanies;
      }
      return this.filteredCompanies.slice(0, COLLAPSED_COMPANY_COUNT);
    },
    hiddenCompaniesCount() {
      return this.filteredCompanies.length - this.visibleCompanies.length;
    },
    allCompaniesSelected() {
      return (
        this.companies.length > 0 && this.draftCompanyIds.length === this.companies.length
      );
    },
    someCompaniesSelected() {
      return this.draftCompanyIds.length > 0 && !this.allCompaniesSelected;
    },
  },
  watch: {
    value: {
      handler(isOpen) {
        if (isOpen) {
          this.loadDraft();
        }
      },
      immediate: true,
    },
    from() {
      this.draftFrom = this.from;
    },
    to() {
      this.draftTo = this.to;
    },
  },
  methods: {
    loadDraft() {
      this.draftFrom = this.from;
      this.draftTo = this.to;
      this.draftShifts = [...this.shifts];
      this.draftCompanyIds = [...this.selectedCompanyIds];
      this.draftView = this.selectedView;
      this.companySearch = "";
      this.showAllCompanies = false;
    },
    resetDraft() {
      const today = this.presets.find((preset) => preset.key === "today");
      this.draftFrom = today.from;
      this.draftTo = today.to;
      this.draftShifts = [...ALL_SHIFTS];
      this.draftCompanyIds = this.companies.map((company) => company.id);
      this.draftView = 1;
      this.companySearch = "";
    },
    selectPreset(preset) {
      this.draftFrom = preset.from;
      this.draftTo = preset.to;
    },
    toggleShift(shift) {
      if (this.draftShifts.includes(shift)) {
        if (this.draftShifts.length > 1) {
          this.draftShifts = this.draftShifts.filter((item) => item !== shift);
        }
        return;
      }
      this.draftShifts = [...this.draftShifts, shift].sort((a, b) => a - b);
    },
    toggleCompany(companyId) {
      this.draftCompanyIds = this.draftCompanyIds.includes(companyId)
        ? this.draftCompanyIds.filter((id) => id !== companyId)
        : [...this.draftCompanyIds, companyId];
    },
    toggleAllCompanies() {
      this.draftCompanyIds = this.allCompaniesSelected
        ? []
        : this.companies.map((company) => company.id);
    },
    normalize(text) {
      return this.toLatin(String(text || ""))
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/đ/g, "dj")
        .trim();
    },
    apply() {
      this.$emit("apply", {
        from: this.draftFrom,
        to: this.draftTo,
        shifts: this.draftShifts,
        companyIds: this.draftCompanyIds,
        view: this.draftView,
      });
      this.model = false;
    },
  },
};
</script>

<style lang="scss">
.rm-filter-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.rm-filter-shifts {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}

.rm-theme .rm-filter-shift {
  height: 48px;
  border-radius: 12px;
  border: 1px solid var(--rm-border-strong);
  background: var(--rm-surface);
  color: var(--rm-chip-text);
  font-size: 16px;
  font-weight: 500;

  &.is-active {
    border: 2px solid var(--rm-accent);
    background: var(--rm-accent-soft);
    color: var(--rm-accent-strong);
    font-weight: 700;
  }
}

.rm-filter-view {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 52px;
  padding: 0 14px;
  border-radius: 12px;
  border: 1px solid var(--rm-border-strong);
  background: var(--rm-surface);

  &__value {
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
  }

  &__select {
    appearance: none;
    -webkit-appearance: none;
    border: none;
    background: transparent;
    color: var(--rm-text);
    font-size: 15px;
    font-weight: 600;
    text-align: right;
    text-align-last: right;
    max-width: 60vw;
    outline: none;
  }
}

.rm-filter-companies {
  display: flex;
  flex-direction: column;

  &__header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
  }

  &__empty {
    padding: 8px 0;
  }

  &__more {
    align-self: flex-start;
    padding-left: 0 !important;
  }
}

.rm-filter-company {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  font-size: 15px;
  cursor: pointer;

  input {
    width: 20px;
    height: 20px;
    margin: 0;
    flex-shrink: 0;
  }

  &--all {
    font-weight: 600;
    border-bottom: 1px solid var(--rm-divider);
  }
}
</style>
